import { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";

import { normalizeLead } from "./LeadUtils";
import { useSettings } from "../../../context/SettingsContext";
import LeadRow from "./LeadRow";
import FilterPopover from "./FilterPopover";
import { ContactInfoPopup } from "../Followup/components";
import EmptyLeadsState from "../../Leads/EmptyLeads";
import { getUsersByCapabilitiesApi } from "../../../api/users.api";
import { AuthContext } from "../../../context/AuthContext";
import AddLeadModal from "./AddLeadModal";

const headerText = (txt) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.5,
      color: "#64748B",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}
  >
    {txt}
  </Typography>
);

export default function LeadsManagementNewUI() {
  const { leads, updateLeadField, createLead } = useOutletContext();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const myUserId = user?._id || "";
  const { services = [], lead_features } = useSettings();

  const perPage = 25;
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filtersOpenEl, setFiltersOpenEl] = useState(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [selectedSource, setSelectedSource] = useState("all");

  // Contact info popup state
  const [contactPopupOpen, setContactPopupOpen] = useState(false);
  const [selectedContactInfo, setSelectedContactInfo] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState("all");
  const [selectedTelecaller, setSelectedTelecaller] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedResponse, setSelectedResponse] = useState("all");
  const [leadManagers, setLeadManagers] = useState([]);
  const [leadManagersLoading, setLeadManagersLoading] = useState(false);
  const [myLeadsOnly, setMyLeadsOnly] = useState(false);

  const sourcesFromSettings = useMemo(() => {
    const list = lead_features?.sources || [];
    return [
      "all",
      ...new Set(
        list
          .map((s) => (typeof s === "string" ? s : s?.key || s?.name))
          .filter(Boolean),
      ),
    ];
  }, [lead_features]);

  useEffect(() => {
    const run = async () => {
      try {
        setLeadManagersLoading(true);
        const res = await getUsersByCapabilitiesApi(["lead_management"]);
        setLeadManagers(res?.data?.data || []);
      } finally {
        setLeadManagersLoading(false);
      }
    };
    run();
  }, []);

  const telecallerOptionsFromAccess = useMemo(() => {
    const list = Array.isArray(leadManagers) ? leadManagers : [];
    return list
      .filter((u) => u && u._id && u.name)
      .map((u) => ({
        id: u._id,
        label: `${u.name}${u.team ? ` (${u.team})` : ""}`,
        name: u.name,
        email: u.email,
        team: u.team,
      }));
  }, [leadManagers]);

  const [pending, setPending] = useState({
    dateRangeStart: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date;
    })(),
    dateRangeEnd: new Date(),
    selectedSource: "all",
    selectedTreatment: "all",
    selectedTelecaller: "all",
    selectedPriority: "all",
    selectedResponse: "all",
    myLeadsOnly: false,
  });

  const normalized = useMemo(
    () => (Array.isArray(leads) ? leads.map(normalizeLead) : []),
    [leads],
  );

  const cap = (s) =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const treatments = useMemo(() => {
    const list = (services || []).filter((x) => x && x !== "—").map(cap);
    return ["all", ...new Set(list)];
  }, [services]);

  const navigate = useNavigate();

  const responseLabels = ["New", "Cold", "Hot", "Warm", "Dropped", "Others"];

  // Fixed status options for the main Leads table with full configuration
  const fixedStatusOptions = [
    {
      key: "new",
      label: "New",
      color: "#0051FF",
      bgcolor: "#E6F2FF",
    },
    {
      key: "cold",
      label: "Cold",
      color: "#0097A7",
      bgcolor: "#E0F7FA",
    },
    {
      key: "warm",
      label: "Warm",
      color: "#F59E0B",
      bgcolor: "#FEF3C7",
    },
    {
      key: "hot",
      label: "Hot",
      color: "#EF4444",
      bgcolor: "#FEE2E2",
    },
    {
      key: "dropped",
      label: "Dropped",
      color: "#6B7280",
      bgcolor: "#F3F4F6",
    },
  ];

  const defaultFilters = {
    dateRangeStart: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date;
    })(),
    dateRangeEnd: new Date(),
    selectedSource: "all",
    selectedTreatment: "all",
    selectedTelecaller: "all",
    selectedPriority: "all",
    selectedResponse: "all",
    myLeadsOnly: false,
  };

  const handleStatusChange = async (leadId, statusKey) => {
    await updateLeadField(leadId, "current_status", {
      key: statusKey,
      type: "grade",
    });
  };

  const handleTelecallerChange = async (leadId, userId) => {
    await updateLeadField(leadId, "assign_employee", userId);
  };

  const handleContactInfoClick = (contactInfo) => {
    setSelectedContactInfo(contactInfo);
    setContactPopupOpen(true);
  };

  const handleContactPopupClose = () => {
    setContactPopupOpen(false);
    setSelectedContactInfo(null);
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();

    return normalized.filter((l) => {
      const searchTxt =
        `${l.name} ${l.phone} ${l.email} ${l.city} ${l.source}`.toLowerCase();
      if (qq && !searchTxt.includes(qq)) return false;

      // Date range filtering
      if (dateRangeStart && dateRangeEnd && l.createdAt) {
        const leadDate = new Date(l.createdAt);
        const start = new Date(dateRangeStart);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRangeEnd);
        end.setHours(23, 59, 59, 999);
        if (leadDate < start || leadDate > end) return false;
      }

      if (
        selectedSource !== "all" &&
        String(l.source || "").toLowerCase() !== selectedSource.toLowerCase()
      )
        return false;

      if (
        selectedTreatment !== "all" &&
        String(l.treatmentType || "").toLowerCase() !==
          selectedTreatment.toLowerCase()
      )
        return false;

      const leadTelecallerId = l.telecallerId || "";
      if (
        selectedTelecaller !== "all" &&
        leadTelecallerId !== selectedTelecaller
      )
        return false;

      const pr = String(l.priority || "").toUpperCase();
      if (selectedPriority !== "all" && pr !== selectedPriority) return false;

      if (
        selectedResponse !== "all" &&
        String(l.responseLabel || "").toLowerCase() !==
          selectedResponse.toLowerCase()
      )
        return false;

      if (myLeadsOnly) {
        if (!myUserId || leadTelecallerId !== myUserId) return false;
      } else {
        if (
          selectedTelecaller !== "all" &&
          leadTelecallerId !== selectedTelecaller
        )
          return false;
      }

      return true;
    });
  }, [
    normalized,
    q,
    dateRangeStart,
    dateRangeEnd,
    selectedSource,
    selectedTreatment,
    selectedTelecaller,
    selectedPriority,
    selectedResponse,
    myLeadsOnly,
    myUserId,
  ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const clampedPage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (clampedPage - 1) * perPage,
    clampedPage * perPage,
  );

  const applyPending = () => {
    setDateRangeStart(pending.dateRangeStart);
    setDateRangeEnd(pending.dateRangeEnd);
    setSelectedSource(pending.selectedSource);
    setSelectedTreatment(pending.selectedTreatment);
    setSelectedTelecaller(pending.selectedTelecaller);
    setSelectedPriority(pending.selectedPriority);
    setSelectedResponse(pending.selectedResponse);
    setMyLeadsOnly(!!pending.myLeadsOnly);
    setPage(1);
    setFiltersOpenEl(null);
  };

  const exportCsv = () => {
    const headers = [
      "id",
      "name",
      "phone",
      "email",
      "location",
      "source",
      "treatmentType",
      "telecaller",
      "callStatus",
      "createdAt",
    ];
    const csv = [
      headers.join(","),
      ...filtered.map((r) =>
        headers
          .map((h) => `"${String(r[h] || "").replaceAll('"', '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "#F8FAFC",
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3, flexWrap: "nowrap", gap: 2 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0F172A",
              lineHeight: 1.3,
            }}
          >
            Leads Management
          </Typography>
          <Typography
            sx={{ fontSize: 12, fontWeight: 400, color: "#64748B", mt: 0.25 }}
          >
            Total {(leads?.length || 0).toLocaleString("en-IN")} prospect
            records
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <TextField
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search leads, phone, email..."
            size="small"
            sx={{
              width: 220,
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                borderRadius: 999,
                bgcolor: "#fff",
                height: 40,
                fontSize: 13,
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
                "&::placeholder": { color: "#94A3B8", opacity: 1 },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <IconButton
            onClick={(e) => {
              setPending({
                dateRangeStart,
                dateRangeEnd,
                selectedSource,
                selectedTreatment,
                selectedTelecaller,
                selectedPriority,
                selectedResponse,
                myLeadsOnly,
              });
              setFiltersOpenEl(e.currentTarget);
            }}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: "#fff",
              flexShrink: 0,
              "&:hover": { bgcolor: "#5B21B6" },
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            <FilterListIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <IconButton
            onClick={exportCsv}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "#fff",
              color: "#64748B",
              border: "1px solid #E2E8F0",
              flexShrink: 0,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddLeadOpen(true)}
            sx={{
              height: 40,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: theme.palette.primary.main,
              fontSize: 13,
              px: 2,
              boxShadow: "none",
              flexShrink: 0,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#5B21B6", boxShadow: "none" },
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                boxShadow: "none",
              },
            }}
          >
            Add New Lead
          </Button>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #E2E8F0",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-track": {
              bgcolor: "#F1F5F9",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#CBD5E1",
              borderRadius: 4,
              "&:hover": { bgcolor: "#94A3B8" },
            },
          }}
        >
          <Table sx={{ minWidth: 1200, width: "100%", tableLayout: "auto" }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "#fff",
                  "& th": {
                    borderBottom: "1px solid #E2E8F0",
                    py: 1.25,
                    px: 1,
                  },
                }}
              >
                <TableCell>{headerText("DATE")}</TableCell>
                <TableCell>{headerText("SOURCE")}</TableCell>
                <TableCell>{headerText("NAME")}</TableCell>
                <TableCell>{headerText("CONTACT")}</TableCell>
                <TableCell>{headerText("TREATMENT TYPE")}</TableCell>
                <TableCell>{headerText("TELECALLER")}</TableCell>
                <TableCell>{headerText("FOLLOW-UP 1")}</TableCell>
                <TableCell>{headerText("FOLLOW-UP 2")}</TableCell>
                <TableCell>{headerText("FOLLOW-UP 3")}</TableCell>
                <TableCell>{headerText("FOLLOW-UP 4")}</TableCell>
                <TableCell>{headerText("STATUS")}</TableCell>
                <TableCell>
                  {headerText("ACTION")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ border: "none" }}>
                    <EmptyLeadsState />
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((l) => (
                  <LeadRow
                    key={l._id}
                    l={l}
                    telecallers={telecallerOptionsFromAccess}
                    treatments={treatments.filter((x) => x !== "all")}
                    statusOptions={fixedStatusOptions}
                    onViewDetails={(lead) => navigate(`/leads/${lead._id}`)}
                    onStatusChange={handleStatusChange}
                    onTelecallerChange={handleTelecallerChange}
                    onContactInfoClick={handleContactInfoClick}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#fff",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: "#94A3B8",
              letterSpacing: 0.3,
            }}
          >
            SHOWING{" "}
            {filtered.length === 0 ? 0 : (clampedPage - 1) * perPage + 1}-
            {Math.min(clampedPage * perPage, filtered.length)} OF{" "}
            {filtered.length} LEADS
          </Typography>

          <Pagination
            page={clampedPage}
            count={pageCount}
            onChange={(_, v) => setPage(v)}
            shape="rounded"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: 12,
                minWidth: 28,
                height: 28,
                color: "#64748B",
                "&:hover": { bgcolor: "#F1F5F9" },
              },
              "& .Mui-selected": {
                bgcolor: `${theme.palette.primary.main} !important`,
                color: "#fff !important",
                "&:hover": { bgcolor: `${theme.palette.primary.dark} !important` },
              },
            }}
          />
        </Box>
      </Paper>

      <FilterPopover
        open={Boolean(filtersOpenEl)}
        anchorEl={filtersOpenEl}
        onClose={() => setFiltersOpenEl(null)}
        pending={pending}
        setPending={setPending}
        applyPending={applyPending}
        clearPending={() => setPending(defaultFilters)}
        treatments={treatments}
        telecallers={telecallerOptionsFromAccess}
        responseLabels={responseLabels}
        sources={sourcesFromSettings}
        myUserId={myUserId}
      />

      <AddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        telecallers={telecallerOptionsFromAccess}
        treatments={treatments}
        sources={sourcesFromSettings}
        existingLeads={leads}
        onSave={async (data) => {
          const payload = {
            client_name: data.fullName?.trim(),
            contact: data.phone?.trim(),
            email: data.email?.trim(),
            assign_employee: data.telecaller || undefined,
            services: Array.isArray(data.treatmentTypes)
              ? data.treatmentTypes
              : [],
            custom_fields: {
              lead_source: data.source || undefined,
              city: data.location || undefined,
              message: data.notes || undefined,
            },
          };

          await createLead(payload);
        }}
      />

      {/* Contact Info Popup */}
      <ContactInfoPopup
        open={contactPopupOpen}
        onClose={handleContactPopupClose}
        contact={selectedContactInfo}
      />
    </Box>
  );
}
